import type { PermissionKey } from '../rbac'
import { RESOURCE_MAP } from '../rbac'

/**
 * AI gezinme aracı (`navigate`) — ajanın kullanıcıyı götürebileceği sayfalar ve URL filtreleri.
 * Sayfalar RESOURCE_MAP'ten türetilir (nav + yetki tek kaynak); yalnızca URL'den filtre okuyan sayfalar
 * AI_NAV_FILTERS'a yazılır (backend doğrular, sayfa `useSearchParams` ile okur) ve detay sayfaları AI_NAV_DETAIL'dedir.
 */

export interface AiNavPage {
  route: string
  /** Görünen ad (i18n değil; ajanın sayfayı seçmesi için) */
  title: string
  /** Sayfayı görme yetkisi; yoksa herkes */
  permission?: PermissionKey
}

/** Sayfa → URL'den okunan filtre anahtarları. Yeni sayfada URL filtresi desteği eklenince buraya yaz. */
export const AI_NAV_FILTERS: Readonly<Record<string, readonly string[]>> = {
  '/projects': ['status', 'search'],
  '/customers': ['search', 'sector', 'tag'],
}

/** Liste sayfası → detay sayfası (`:id`) */
export const AI_NAV_DETAIL: Readonly<Record<string, string>> = {
  '/projects': '/projects/:id',
  '/customers': '/customers/:id',
}

/** Oluşturma ucu (API yolu) → oluşan kaydın detay sayfası (`:id`); onaylı önizleme kartındaki "Kaydı aç" bağlantısı */
export const AI_RECORD_DETAIL: Readonly<Record<string, string>> = {
  '/proposals': '/proposals/:id',
  '/projects': '/projects/:id',
  '/customers': '/customers/:id',
  '/users': '/users/:id',
  '/sub-consultants': '/sub-consultants/:id',
}

export const AI_NAV_PAGES: readonly AiNavPage[] = [
  { route: '/dashboard', title: 'Anasayfa' },
  ...RESOURCE_MAP.flatMap((s) => s.pages)
    .filter((p): p is typeof p & { route: string } => !!p.route)
    .map((p) => ({ route: p.route, title: p.page, permission: p.read })),
]

export class AiNavError extends Error {}

const ID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

export interface AiNavTarget {
  /** Arayüzde `navigate()` ile açılacak yol (sorgu dizesi dahil) */
  path: string
  /** Kullanıcıya gösterilecek ad */
  label: string
}

/** Ajanın isteğini doğrular; yetkisiz sayfa, bilinmeyen filtre ya da geçersiz id → AiNavError */
export function resolveAiNavTarget(
  input: { page: string; id?: string; filters?: Record<string, string> },
  perms: ReadonlySet<PermissionKey>,
): AiNavTarget {
  const page = AI_NAV_PAGES.find((p) => p.route === input.page)
  if (!page || (page.permission && !perms.has(page.permission))) {
    throw new AiNavError(`Sayfa yok ya da yetkiniz yok: ${input.page}`)
  }
  let path = page.route
  if (input.id) {
    const detail = AI_NAV_DETAIL[page.route]
    if (!detail) throw new AiNavError(`${page.route} için detay sayfası yok.`)
    if (!ID_RE.test(input.id)) throw new AiNavError('Geçersiz kayıt id (UUID olmalı).')
    path = detail.replace(':id', input.id)
  }
  const query = new URLSearchParams()
  const entries = Object.entries(input.filters ?? {}).filter(([, v]) => v !== '')
  if (entries.length) {
    if (input.id) throw new AiNavError('Detay sayfasına filtre verilemez.')
    const allowed = AI_NAV_FILTERS[page.route] ?? []
    const unknown = entries.map(([k]) => k).filter((k) => !allowed.includes(k))
    if (unknown.length) {
      throw new AiNavError(`${page.route} şu filtreleri desteklemiyor: ${unknown.join(', ')}. ${allowed.length ? `Desteklenen: ${allowed.join(', ')}` : 'Bu sayfa URL filtresi desteklemiyor.'}`)
    }
    for (const [k, v] of entries) query.set(k, v)
  }
  const qs = query.toString()
  return { path: qs ? `${path}?${qs}` : path, label: page.title }
}
